import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedSection
 * ---------------
 * Wraps a block of content in an IntersectionObserver-driven fade-up entrance.
 * - Reveals when the element enters the viewport (threshold 0.12).
 * - Supports a `delay` prop (ms) for staggered animations.
 * - Respects `prefers-reduced-motion`.
 */
export function AnimatedSection({ children, delay = 0, className = '', as: Tag = 'section', ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect reduced-motion preference
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 500ms cubic-bezier(.2,.8,.2,1) ${delay}ms, transform 500ms cubic-bezier(.2,.8,.2,1) ${delay}ms`,
        willChange: visible ? 'auto' : 'transform, opacity',
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * useCountUp
 * ----------
 * Smoothly animates a number from `start` to `target` over `duration` ms.
 * Ease-out cubic. Returns the current animated value.
 */
export function useCountUp(target, { duration = 700, start = 0, enabled = true } = {}) {
  const [value, setValue] = useState(enabled ? start : target);
  const rafRef = useRef();

  useEffect(() => {
    if (!enabled) { setValue(target); return; }
    const t0 = performance.now();
    const from = value;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, enabled]);

  return value;
}
